import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import { ProductType } from "../../../utils/enums/ProductType";
import { handleDayOfMonth, handleHourAndMinutes, handleMonthString } from "../../../utils/Functions";
import { useConsultsStore } from "../../../utils/stores/useConsultsStore";
import { useFilesStore } from "../../../utils/stores/useFilesStore";
import { useProductsStore } from "../../../utils/stores/useProductsStore";
import { Appointment } from "../../appointments/types/Appointment";
import { ExpertRole } from "../../experts/types/ExpertRole";
import { MetabolicUserType } from "../types/MetabolicUserType";

/**
 * Hook to check if the user has uploaded medical files after metabolic health membership creation.
 */
export const useFilesUploaded = () => {
    const { filesData } = useFilesStore();
    const { productsData } = useProductsStore();

    const userFiles = filesData?.userFiles;
    const membership = productsData?.membership;

    return userFiles && userFiles.length > 0 && membership
        ? new Date(userFiles[0].uploaded_at) > new Date(membership.created_at)
        : false;
};

/**
 * Hook to calculate the percentage of completion based on uploaded files, health assessment, consults and metabolic product type.
 *
 * @returns {object} An object containing:
 *   - `fullPercentage`: Overall completion percentage
 *   - `medicalHistoryPercentage`: Completion percentage for medical history
 *   - `medicalEquipePercentage`: Completion percentage for the medical team section
 */
export const useCompletionPercentage = (): {
    fullPercentage: number,
    medicalHistoryPercentage: number,
    medicalEquipePercentage: number,
} => {
    const filesUploaded = useFilesUploaded();
    const { isHealthAssessmentCompleted } = useHealthAssessment()
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { shouldPushBooking } = useSpecialistConsult();
    const { shouldPushNutritionistBooking } = useNutritionistConsult();
    const metabolicUserType = useMetabolicUserType();

    const totaleMultiplier =
        metabolicUserType === MetabolicUserType.Full
            ? 25
            : metabolicUserType === MetabolicUserType.Lite
                ? 100 / 3
                : (metabolicUserType === MetabolicUserType.Hook ? 100 : 0);

    const equipeMultiplier = metabolicUserType === MetabolicUserType.Full ? 50 : 100;

    const stepsCompleted = [
        healthAssessmentCompleted,
        filesUploaded,
        !shouldPushBooking,
        !shouldPushNutritionistBooking,
    ].filter(Boolean).length;

    const fullPercentage = stepsCompleted * totaleMultiplier;

    const stepsHistoryCompleted = [
        healthAssessmentCompleted,
        filesUploaded,
    ].filter(Boolean).length;

    const medicalHistoryPercentage = stepsHistoryCompleted * 50;

    const stepsEquipeCompleted = [
        !shouldPushBooking,
        !shouldPushNutritionistBooking,
    ].filter(Boolean).length;

    const medicalEquipePercentage = stepsEquipeCompleted * equipeMultiplier;

    return { fullPercentage, medicalHistoryPercentage, medicalEquipePercentage };
};

/**
 * Hook to manage actions and UI logic for the Metabolic Health Control Panel.
 */
export const useControlPanel = () => {
    const { shouldPushBooking, hasFutureSpecialistAppointment } = useSpecialistConsult()
    const { shouldPushNutritionistBooking, hasFutureNutritionistAppointment, bookNutriAvailable } = useNutritionistConsult()

    const { consultsData } = useConsultsStore();
    const specConsultsLeft = consultsData?.specialistFutureConsults || [];
    const specOldConsults = consultsData?.allPastAppointments?.filter(app => (app.expert.role === ExpertRole.Specialist || app.expert.role === ExpertRole.Physician))
    const nutriConsultsLeft = consultsData?.nutritionistFutureConsults || []
    const nutriOldConsults = consultsData?.allPastAppointments?.filter(app => app.expert.role === ExpertRole.Nutritionist)

    const metabolicUserType = useMetabolicUserType()

    const pageSingleElement = (id: string) => {
        if (id === "healthAssessment") {
            return (
                <SingleLineText text="📝 Rispondi all'Health Assessment" />
            )
        }
        if (id === "uploadReport") {
            return (
                <SingleLineText text="📄 Carica i tuoi referti" />
            )
        }
        if (id === "bookDoctor") {
            if (shouldPushBooking) {
                return (
                    <SingleLineText text="👩‍⚕️️ Prenota consulto con medico" />
                )
            } else if (hasFutureSpecialistAppointment) {
                return (
                    <ConsultInfo consult={specConsultsLeft[0]} />
                )
            } else if (specOldConsults) {
                return (
                    <ConsultInfo consult={specOldConsults[0]} isOld={true} />
                )
            }
        }
        if (id === "bookNutri") {
            if (shouldPushNutritionistBooking) {
                return (
                    <SingleLineText text="👨‍⚕️️  Prenota consulto con nutrizionista" />
                )
            } else if (hasFutureNutritionistAppointment) {
                return (
                    <ConsultInfo consult={nutriConsultsLeft[0]} />
                )
            } else if (nutriOldConsults) {
                return (
                    <ConsultInfo consult={nutriOldConsults[0]} isOld={true} />
                )
            }
        }
    }

    const SingleLineText: React.FC<{ text: string }> = ({ text }) => {
        return (
            <span>{text}</span>
        )
    }

    const ConsultInfo: React.FC<{ consult: Appointment, isOld?: boolean }> = ({ consult, isOld = false }) => {
        return (
            <div className="w-full flex items-center justify-between">
                <div className="flex items-center">
                    <div className="font-medium text-[15px]">
                        {consult.expert.data.name} {consult.expert.data.family_name}
                    </div>
                    <div className="font-bold text-[10px] text-holifya-blue ml-[10px]">
                        {consult.expert.role === ExpertRole.Specialist ? "Medico" : consult.expert.role === ExpertRole.Physician ? "Endocrinologo" : "Nutrizionista"}
                    </div>
                </div>
                {
                    isOld ? <div className="font-medium text-[12px]">
                        Completato
                    </div> : <div className="font-medium text-[12px]">
                        <strong>{getDisplayDate(consult)}</strong> {handleHourAndMinutes(consult.date)}
                    </div>}
            </div>
        )
    }

    return { pageSingleElement };
}

export const ConsultCard: React.FC<{ consult: Appointment }> = ({
    consult,
}) => {
    const isUrgent = consult.remainingMinutes <= 1440

    return (
        <div className={`w-full px-[10px] py-[11px] rounded-[5px] border border-solid ${isUrgent ? "border-holifya-blue" : "border-[#E5E5E5]"} shadow-platform-new flex items-center justify-between ${isUrgent ? "bg-[#D7E7FF]" : "bg-white"}`}>
            <div className="font-medium text-[14px]">
                <div>{consult.expert?.data?.name} {consult.expert?.data?.family_name}</div>
                <div className="text-[10px] text-holifya-blue">
                    {consult.expert?.role === ExpertRole.Specialist ? "Medico" : consult.expert?.role === ExpertRole.Physician ? "Endocrinologo" : "Nutrizionista"}
                </div>
            </div>
            <div className="flex">
                <div className="flex items-center mr-[10px] font-medium text-[10px]">
                    <div>
                        <strong>{getDisplayDate(consult)}</strong> {handleHourAndMinutes(consult.date)}
                    </div>
                </div>
                <div className="w-[86px] h-[26px]">
                    <PrimaryButton
                        text="Connettiti"
                        pointer={true}
                        disabled={false}
                        uppercase={false}
                        fontSize={10}
                        onClick={() => window.open(consult.connectUrl, "_blank")}
                        className="!rounded-[3px]"
                    />
                </div>
            </div>
        </div>
    )
}

const getDisplayDate = (consult: Appointment) => {
    const currentDate = new Date();
    const appointmentDate = new Date(consult.date);

    currentDate.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);

    const timeDiff = appointmentDate.getTime() - currentDate.getTime();
    const dayDiff = timeDiff / (1000 * 3600 * 24);

    if (dayDiff === 0) {
        return "Oggi";
    } else if (dayDiff === 1) {
        return "Domani";
    } else {
        return `${handleDayOfMonth(consult.date)} ${handleMonthString(consult.date)}`;
    }
};

/**
 * Determines if the current user is a metabolic health user.
 *
 * This hook checks if the user's membership sub-type is either 
 * `SubMetabolicHealth` or `SubMetabolicHealthLite`, or if the 
 * uses bought a `metabolicHook` product with is a consult with the spacialist.
 *
 * @returns {boolean} `true` if the user qualifies as a metabolic health user, otherwise `false`.
 */
export const useIsMetabolicUser = (): boolean => {
    const { productsData } = useProductsStore()
    const membership = productsData.membership
    const metabolicHook = productsData.metabolicHook

    return !!((membership && (membership.sub_type === ProductType.SubMetabolicHealth || membership.sub_type === ProductType.SubMetabolicHealthLite)) || (metabolicHook && metabolicHook === true))
}

export const useMetabolicUserType = () => {
    const { productsData } = useProductsStore()
    const membership = productsData.membership
    const metabolicHook = productsData.metabolicHook

    if (membership && membership.sub_type === ProductType.SubMetabolicHealth) {
        return MetabolicUserType.Full
    } else if (membership && membership.sub_type === ProductType.SubMetabolicHealthLite) {
        return MetabolicUserType.Lite
    } else if (metabolicHook && metabolicHook === true) {
        return MetabolicUserType.Hook
    } else {
        return MetabolicUserType.None
    }
}
